import { useMemo } from "react";
import { useWatch } from "react-hook-form";
import { useIntl, MessageDescriptor } from "react-intl";

import { ReactComponent as PlaneIcon } from "assets/images/iconsV2/plane.svg";
import { ReactComponent as CargoShipIcon } from "assets/images/iconsV2/cargo-ship.svg";
import { ReactComponent as TruckIcon } from "assets/images/iconsV2/truck.svg";
import { ReactComponent as ParcelIcon } from "assets/images/iconsV2/package.svg";
import { useGetFreightModesConfig } from "api/queries/location/useGetFreightModesConfig";
import { useGetServiceDetailsConfig } from "api/queries/location/useGetServiceDetailsConfig";
import { AvailableFreightMode } from "api/response/location.response";
import {
  KNOWN_FREIGHT_API_TYPES,
  ORDERED_FREIGHT_CATEGORIES,
  type FreightModeCategory,
} from "@RequestFormV2/utils/freightModeUtils";
import { normalizeServiceTypeOptions } from "@RequestFormV2/utils/serviceTypeOptions";

export type FreightModeKey = FreightModeCategory;

const TYPE_CONFIG: Record<
  FreightModeKey,
  { labelDescriptor: MessageDescriptor; Icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>> }
> = {
  air: { labelDescriptor: { id: "request-form.service.freightMode.air", defaultMessage: "Air" }, Icon: PlaneIcon },
  ocean: { labelDescriptor: { id: "request-form.service.freightMode.ocean", defaultMessage: "Ocean" }, Icon: CargoShipIcon },
  road: { labelDescriptor: { id: "request-form.service.freightMode.land", defaultMessage: "Land" }, Icon: TruckIcon },
  others: { labelDescriptor: { id: "request-form.service.freightMode.parcel", defaultMessage: "Parcel" }, Icon: ParcelIcon },
};

export function getSelectableValues(modes: AvailableFreightMode[]): string[] {
  return modes.map((mode) => mode.value);
}

export function useServiceData() {
  const intl = useIntl();
  const location = useWatch({ name: "organizational.location" });

  const { data: freightModesConfigData, isLoading: isLoadingFreightModes } = useGetFreightModesConfig(location ?? "", {
    enabled: !!location,
  });

  const { data: serviceDetailsConfig, isLoading: isLoadingServiceDetails } = useGetServiceDetailsConfig(location ?? "", {
    enabled: !!location,
  });

  const freightModeEntries = useMemo(() => {
    const modes = freightModesConfigData?.freight_modes?.available_modes ?? [];
    const byApiType = modes.reduce<Record<string, typeof modes>>((acc, mode) => {
      if (!acc[mode.type]) acc[mode.type] = [];
      acc[mode.type].push(mode);
      return acc;
    }, {});

    const grouped = {
      air: byApiType.air ?? [],
      ocean: byApiType.ocean ?? [],
      road: [...(byApiType.road ?? []), ...(byApiType.rail ?? [])],
      others: Object.entries(byApiType)
        .filter(([type]) => !KNOWN_FREIGHT_API_TYPES.has(type))
        .flatMap(([, typeModes]) => typeModes),
    };

    return ORDERED_FREIGHT_CATEGORIES.map((key) => ({
      key,
      modes: grouped[key],
      label: intl.formatMessage(TYPE_CONFIG[key].labelDescriptor),
      Icon: TYPE_CONFIG[key].Icon,
      selectableValues: getSelectableValues(grouped[key]),
    })).filter(({ modes: m }) => m.length > 0);
  }, [freightModesConfigData, intl]);

  const serviceTypeOptions = useMemo(
    () => normalizeServiceTypeOptions(serviceDetailsConfig?.service_options?.service_type_options, intl),
    [serviceDetailsConfig, intl],
  );

  return useMemo(
    () => ({
      freightModesConfig: freightModesConfigData?.freight_modes,
      freightModeEntries,
      isLoadingFreightModes,
      serviceDetailsConfig,
      serviceTypeOptions,
      isLoadingServiceDetails,
    }),
    [
      freightModesConfigData,
      freightModeEntries,
      isLoadingFreightModes,
      serviceDetailsConfig,
      serviceTypeOptions,
      isLoadingServiceDetails,
    ],
  );
}
